/**
 * Production dictation deps.
 *
 * The two SYSTEM BOUNDARIES the `useDictation` hook injects: a browser
 * `MediaRecorder` factory and the `fetch` closure that POSTs to
 * `/transcribe`. Verified by manual demo + HITL review (the locked
 * test-depth decision, identical to the engine adapters). The hook's
 * orchestration is unit-tested via deterministic fakes; this file is the
 * production wiring.
 */
import AppConfig from '~/utils/appconfig';
import type { DictationRecorder } from './useDictation';

/** Prefer opus-in-webm (broadly supported by Whisper); fall back to
 *  whatever the browser will give us. */
function pickMimeType(): string {
    const candidates = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/mp4',
    ];
    for (const candidate of candidates) {
        if (
            typeof MediaRecorder !== 'undefined' &&
            MediaRecorder.isTypeSupported(candidate)
        ) {
            return candidate;
        }
    }
    return '';
}

/**
 * Real browser recorder. Requests the mic on `start`; resolves `stop` with
 * the captured bytes + mime; releases the stream on stop or cancel.
 */
export function createBrowserRecorderFactory(): () => DictationRecorder {
    return (): DictationRecorder => {
        let recorder: MediaRecorder | null = null;
        let stream: MediaStream | null = null;
        const chunks: Blob[] = [];
        let stopped: Promise<void> | null = null;
        let resolveStopped: (() => void) | null = null;

        return {
            async start(): Promise<void> {
                const desired = pickMimeType();
                stream = await navigator.mediaDevices.getUserMedia({
                    audio: true,
                });
                // The mic is live the instant getUserMedia resolves. Any
                // failure constructing or starting the recorder must release
                // the tracks before rethrowing, or the mic stays hot.
                try {
                    recorder = new MediaRecorder(
                        stream,
                        desired ? { mimeType: desired } : undefined
                    );
                    stopped = new Promise<void>((resolve) => {
                        resolveStopped = resolve;
                    });
                    recorder.addEventListener('dataavailable', (e) => {
                        if (e.data && e.data.size > 0) {
                            chunks.push(e.data);
                        }
                    });
                    recorder.addEventListener('stop', () => {
                        stream?.getTracks().forEach((t) => t.stop());
                        resolveStopped?.();
                    });
                    recorder.start();
                } catch (e) {
                    stream.getTracks().forEach((t) => t.stop());
                    recorder = null;
                    stream = null;
                    stopped = null;
                    resolveStopped = null;
                    throw e;
                }
            },
            async stop(): Promise<{
                readonly audio: Uint8Array;
                readonly mimeType: string;
            }> {
                if (!recorder) {
                    throw new Error('recorder not started');
                }
                const mimeType = recorder.mimeType || pickMimeType();
                recorder.stop();
                await stopped;
                const blob = new Blob(chunks, { type: mimeType });
                const buffer = await blob.arrayBuffer();
                return { audio: new Uint8Array(buffer), mimeType };
            },
            cancel(): void {
                try {
                    recorder?.stop();
                } catch {
                    /* idempotent best-effort */
                }
                stream?.getTracks().forEach((t) => t.stop());
                chunks.length = 0;
            },
        };
    };
}

/**
 * POST the audio bytes to the stateless `/transcribe` route, with the
 * agent request headers (sign-in token, test user). The audio is
 * in-flight only; the server persists nothing.
 */
export async function transcribeViaRoute(
    audio: Uint8Array,
    mimeType: string,
    signal?: AbortSignal,
    requestHeaders: Record<string, string> = {}
): Promise<{ readonly text: string }> {
    const response = await fetch(AppConfig.agentTranscribeUrl, {
        method: 'POST',
        body: audio as BodyInit,
        headers: {
            ...requestHeaders,
            'content-type': mimeType || 'application/octet-stream',
        },
        signal,
    });
    if (!response.ok) {
        const detail = await response.text().catch(() => response.statusText);
        throw new Error(`transcription failed (${response.status}): ${detail}`);
    }
    const data = (await response.json()) as { text: string };
    return { text: data.text };
}
